import { PLATFORM_TIME_ZONE } from "@repo/app-types/constants/timeZone";
import {
  allScheduleFrequencies,
  allScheduleWeekdays,
  type MonthlySchedule,
  type RecurringSchedule,
  type ScheduleFrequency,
  type ScheduleWeekday,
} from "@repo/app-types/scheduling/recurringSchedule";
import type { DateTime } from "luxon";
import type { FC, ReactNode } from "react";
import {
  SimpleSelect,
  type SimpleSelectOption,
} from "../Select/simpleSelect.js";
import { TimeSelect } from "../Select/timeSelect.js";
import {
  ToggleChipGroup,
  type ToggleChipOption,
} from "../toggleButton/toggleChipGroup.js";

/**
 * The one editor (and option vocabulary) for the shared `RecurringSchedule`
 * union — frequency, weekday chips, monthly run day, run time — used by
 * admin's agent trigger and HQ's knowledge-base sync cadence. Layout is the
 * caller's via the className props; which controls exist per variant, their
 * labels, and the variant-switching rules live here once.
 */

export const scheduleFrequencyLabels: Record<ScheduleFrequency, string> = {
  hourly: "Hourly",
  daily: "Daily",
  weekly: "Weekly",
  monthly: "Monthly",
};

const frequencyOptions: readonly SimpleSelectOption<ScheduleFrequency>[] =
  allScheduleFrequencies.map((frequency) => ({
    value: frequency,
    label: scheduleFrequencyLabels[frequency],
  }));

export const weekdayLabels: Record<ScheduleWeekday, string> = {
  monday: "Monday",
  tuesday: "Tuesday",
  wednesday: "Wednesday",
  thursday: "Thursday",
  friday: "Friday",
  saturday: "Saturday",
  sunday: "Sunday",
};

const weekdayChipOptions: readonly ToggleChipOption<ScheduleWeekday>[] =
  allScheduleWeekdays.map((weekday) => ({
    id: weekday,
    label: weekdayLabels[weekday].slice(0, 3),
  }));

// The monthly "Run day" dropdown flattens the MonthlySchedule union into one
// option list; each option carries the schedule it maps to, so a handler sets
// it without reconstructing the discriminant.
export interface MonthlyScheduleOption extends SimpleSelectOption<string> {
  schedule: MonthlySchedule;
}

export const monthlyScheduleOptions: readonly MonthlyScheduleOption[] = [
  {
    value: "first_day_of_month",
    label: "The first day",
    schedule: { kind: "first_day_of_month" },
  },
  ...allScheduleWeekdays.map((weekday) => ({
    value: `first_weekday_of_month:${weekday}`,
    label: `The first ${weekdayLabels[weekday]}`,
    schedule: { kind: "first_weekday_of_month" as const, weekday },
  })),
  {
    value: "last_day_of_month",
    label: "The last day",
    schedule: { kind: "last_day_of_month" },
  },
];

export function monthlyScheduleToOptionValue(
  schedule: MonthlySchedule,
): string {
  switch (schedule.kind) {
    case "first_day_of_month":
      return "first_day_of_month";
    case "first_weekday_of_month":
      return `first_weekday_of_month:${schedule.weekday}`;
    case "last_day_of_month":
      return "last_day_of_month";
  }
}

/**
 * Switches a schedule to a new frequency, carrying over the run time (every
 * variant but hourly has one) and seeding the new variant's extra fields.
 * `seedTimeOfDay` fills in when switching away from hourly, which carries none.
 */
export function changeScheduleFrequency(
  schedule: RecurringSchedule,
  frequency: ScheduleFrequency,
  seedTimeOfDay: string,
): RecurringSchedule {
  if (schedule.frequency === frequency) {
    return schedule;
  }
  const timeOfDay =
    schedule.frequency === "hourly" ? seedTimeOfDay : schedule.time_of_day;
  switch (frequency) {
    case "hourly":
      return { frequency: "hourly" };
    case "daily":
      return { frequency: "daily", time_of_day: timeOfDay };
    case "weekly":
      return {
        frequency: "weekly",
        weekdays: ["monday"],
        time_of_day: timeOfDay,
      };
    case "monthly":
      return {
        frequency: "monthly",
        schedule: { kind: "first_day_of_month" },
        time_of_day: timeOfDay,
      };
  }
}

/**
 * The zone abbreviation schedules are labelled with — "EST"/"EDT", never the
 * IANA name. Evaluated at `at` so a time the far side of a DST change carries
 * the abbreviation it will actually run under.
 */
export function formatPlatformZoneAbbreviation(at: DateTime): string {
  return at.setZone(PLATFORM_TIME_ZONE).toFormat("ZZZZ");
}

export interface RecurringScheduleFieldsProps {
  value: RecurringSchedule;
  onChange: (schedule: RecurringSchedule) => void;
  isDisabled?: boolean;
  /** Run time seeded when switching away from hourly, which carries none. */
  seedTimeOfDay: string;
  /** Aria label for the frequency select (each surface names it its own way). */
  frequencyLabel?: string;
  /** Container classes — the surfaces' layouts differ (admin stacks, HQ wraps). */
  className: string;
  /** Wraps each dropdown, for a surface's width constraints. */
  controlClassName?: string;
  /** Rendered beside the run-time select (e.g. the zone abbreviation). */
  timeOfDayTrailing?: ReactNode;
  /** Rendered last in the container (e.g. HQ's next-run preview). */
  children?: ReactNode;
}

export const RecurringScheduleFields: FC<RecurringScheduleFieldsProps> = (
  props,
) => {
  const {
    value,
    onChange,
    isDisabled,
    seedTimeOfDay,
    frequencyLabel = "Frequency",
    className,
    controlClassName,
    timeOfDayTrailing,
    children,
  } = props;

  return (
    <div className={className}>
      <div className={controlClassName}>
        <SimpleSelect
          label={frequencyLabel}
          isLabelHidden
          options={frequencyOptions}
          value={value.frequency}
          isDisabled={isDisabled}
          onChange={(frequency) =>
            onChange(changeScheduleFrequency(value, frequency, seedTimeOfDay))
          }
        />
      </div>

      {value.frequency === "weekly" ? (
        <ToggleChipGroup
          aria-label="Run days"
          options={weekdayChipOptions}
          value={value.weekdays}
          isDisabled={isDisabled}
          onChange={(weekdays) => {
            // The weekly variant requires at least one day; ignore a
            // deselection that would empty the set.
            if (weekdays.length > 0) {
              onChange({ ...value, weekdays: [...weekdays] });
            }
          }}
        />
      ) : null}

      {value.frequency === "monthly" ? (
        <div className={controlClassName}>
          <SimpleSelect
            label="Run day"
            isLabelHidden
            options={monthlyScheduleOptions}
            value={monthlyScheduleToOptionValue(value.schedule)}
            isDisabled={isDisabled}
            onChange={(optionValue) => {
              const option = monthlyScheduleOptions.find(
                (candidate) => candidate.value === optionValue,
              );
              if (option) {
                onChange({ ...value, schedule: option.schedule });
              }
            }}
          />
        </div>
      ) : null}

      {value.frequency !== "hourly" ? (
        <div className="flex items-center gap-2">
          <TimeSelect
            label="Run time"
            isLabelHidden
            value={value.time_of_day}
            isDisabled={isDisabled}
            onChange={(timeOfDay) =>
              onChange({ ...value, time_of_day: timeOfDay })
            }
          />
          {timeOfDayTrailing}
        </div>
      ) : null}

      {children}
    </div>
  );
};
